'use client';

import React, { useState, useCallback } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { createClient } from '../../../lib/supabase/client';
import { toast } from 'sonner';
import {
  Upload, ArrowRight, ArrowLeft, CheckCircle2, RotateCcw, FileText,
} from 'lucide-react';
import FileUploadZone from './FileUploadZone';
import ColumnMappingPanel from './ColumnMappingPanel';
import ImportPreviewTable from './ImportPreviewTable';
import { ParsedRow, MappedContact, ImportRow, ColumnMapping, LINKEDIN_COLUMN_MAP, ImportStep,  } from './importTypes';

const STEPS: { id: ImportStep; label: string }[] = [
  { id: 'upload', label: 'Upload File' },
  { id: 'mapping', label: 'Map Columns' },
  { id: 'preview', label: 'Preview & Confirm' },
  { id: 'done', label: 'Done' },
];

function autoDetectMappings(columns: string[]): ColumnMapping[] {
  return columns.map((col) => {
    const normalized = col.toLowerCase().trim();
    const field = LINKEDIN_COLUMN_MAP[normalized] ?? '';
    return { csvColumn: col, contactField: field };
  });
}

function applyMappings(row: ParsedRow, mappings: ColumnMapping[]): MappedContact {
  const result: MappedContact = {
    firstName: '', lastName: '', email: '', linkedinUrl: '',
    title: '', company: '', industry: '', country: '', event: '', notes: '', tags: '',
  };
  for (const mapping of mappings) {
    if (mapping.contactField && mapping.csvColumn in row) {
      const val = row[mapping.csvColumn]?.trim() ?? '';
      (result as any)[mapping.contactField] = val;
    }
  }
  return result;
}

function validateRow(mapped: MappedContact): string[] {
  const errors: string[] = [];
  if (!mapped.firstName && !mapped.lastName) {
    errors.push('Missing both First Name and Last Name');
  }
  if (mapped.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mapped.email)) {
    errors.push(`Invalid email format: "${mapped.email}"`);
  }
  return errors;
}

export default function ImportContent() {
  const [step, setStep] = useState<ImportStep>('upload');
  const [fileName, setFileName] = useState('');
  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<ParsedRow[]>([]);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; duplicates: number; errors: number } | null>(null);

  // Parse uploaded file
  const handleFileAccepted = useCallback(async (file: File) => {
    setIsLoading(true);
    setFileName(file.name);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase();
      let rows: ParsedRow[] = [];

      if (ext === 'csv') {
        const text = await file.text();
        const result = Papa.parse<ParsedRow>(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (h) => h.trim(),
        });
        rows = result.data;
      } else {
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json<ParsedRow>(ws, { defval: '' });
      }

      if (rows.length === 0) {
        toast.error('The file appears to be empty or has no data rows.');
        setIsLoading(false);
        return;
      }

      const columns = Object.keys(rows[0]);
      setCsvColumns(columns);
      setRawRows(rows);
      setMappings(autoDetectMappings(columns));
      setStep('mapping');
    } catch (err: any) {
      toast.error('Failed to parse file: ' + (err?.message ?? 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update a single column mapping
  const handleMappingChange = useCallback((csvColumn: string, contactField: string) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.csvColumn === csvColumn ? { ...m, contactField: contactField as keyof MappedContact | '' } : m
      )
    );
  }, []);

  // Build preview rows with duplicate detection
  const buildPreview = useCallback(async () => {
    setIsLoading(true);
    try {
      const supabase = createClient();

      // Fetch existing contacts for duplicate detection
      const { data: existingContacts } = await supabase
        .from('contacts')
        .select('email, linkedin_url, first_name, last_name, company');

      const existingEmails = new Set(
        (existingContacts ?? []).map((c: any) => c.email?.toLowerCase().trim()).filter(Boolean)
      );
      const existingLinkedIn = new Set(
        (existingContacts ?? []).map((c: any) => c.linkedin_url?.toLowerCase().trim()).filter(Boolean)
      );
      const existingNameCompany = new Set(
        (existingContacts ?? []).map((c: any) =>
          `${c.first_name?.toLowerCase().trim()}|${c.last_name?.toLowerCase().trim()}|${c.company?.toLowerCase().trim()}`
        ).filter((k) => k !== '||')
      );

      // Also track duplicates within the file itself
      const seenEmails = new Set<string>();
      const seenLinkedIn = new Set<string>();
      const seenNameCompany = new Set<string>();

      let rows: ImportRow[] = rawRows.map((raw, index) => {
        const mapped = applyMappings(raw, mappings);
        const errors = validateRow(mapped);

        if (errors.length > 0) {
          return { index, raw, mapped, status: 'error', errors };
        }

        const emailKey = mapped.email?.toLowerCase().trim();
        const liKey = mapped.linkedinUrl?.toLowerCase().trim();
        const ncKey = `${mapped.firstName?.toLowerCase().trim()}|${mapped.lastName?.toLowerCase().trim()}|${mapped.company?.toLowerCase().trim()}`;

        // Check DB duplicates
        if (emailKey && existingEmails.has(emailKey)) {
          return { index, raw, mapped, status: 'duplicate', duplicateReason: `Email "${mapped.email}" already exists in your contacts.` };
        }
        if (liKey && existingLinkedIn.has(liKey)) {
          return { index, raw, mapped, status: 'duplicate', duplicateReason: `LinkedIn URL already exists in your contacts.` };
        }
        if (ncKey !== '||' && existingNameCompany.has(ncKey)) {
          return { index, raw, mapped, status: 'duplicate', duplicateReason: `Contact "${mapped.firstName} ${mapped.lastName}" at "${mapped.company}" already exists.` };
        }

        // Check in-file duplicates
        if (emailKey && seenEmails.has(emailKey)) {
          return { index, raw, mapped, status: 'duplicate', duplicateReason: `Duplicate email "${mapped.email}" found in this file.` };
        }
        if (liKey && seenLinkedIn.has(liKey)) {
          return { index, raw, mapped, status: 'duplicate', duplicateReason: `Duplicate LinkedIn URL found in this file.` };
        }
        if (ncKey !== '||' && seenNameCompany.has(ncKey)) {
          return { index, raw, mapped, status: 'duplicate', duplicateReason: `Duplicate name+company "${mapped.firstName} ${mapped.lastName} / ${mapped.company}" found in this file.` };
        }

        if (emailKey) seenEmails.add(emailKey);
        if (liKey) seenLinkedIn.add(liKey);
        if (ncKey !== '||') seenNameCompany.add(ncKey);

        return { index, raw, mapped, status: 'valid' };
      });

      setImportRows(rows);
      setStep('preview');
    } catch (err: any) {
      toast.error('Failed to check duplicates: ' + (err?.message ?? 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [rawRows, mappings]);

  // Confirm and insert valid rows
  const handleConfirmImport = useCallback(async () => {
    setIsLoading(true);
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();

      const validRows = importRows.filter((r) => r.status === 'valid');
      if (validRows.length === 0) {
        toast.info('No valid rows to import.');
        setIsLoading(false);
        return;
      }

      const records = validRows.map((r) => ({
        user_id: user?.id ?? null,
        first_name: r.mapped.firstName || '',
        last_name: r.mapped.lastName || '',
        email: r.mapped.email || '',
        linkedin_url: r.mapped.linkedinUrl || '',
        title: r.mapped.title || '',
        company: r.mapped.company || '',
        industry: r.mapped.industry || '',
        country: r.mapped.country || '',
        event: r.mapped.event || '',
        notes: r.mapped.notes || '',
        tags: r.mapped.tags ? r.mapped.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        outreach_stage: 'New Lead',
        linkedin_status: 'Not Attempted',
        meeting_status: 'None',
        priority: 'Medium',
        priority_score: 50,
      }));

      // Insert in batches of 100
      const BATCH = 100;
      let inserted = 0;
      for (let i = 0; i < records.length; i += BATCH) {
        const batch = records.slice(i, i + BATCH);
        const { error } = await supabase.from('contacts').insert(batch);
        if (error) throw error;
        inserted += batch.length;
      }

      // Log the import
      await supabase.from('import_logs').insert({
        user_id: user?.id ?? null,
        file_name: fileName,
        total_rows: importRows.length,
        imported_rows: inserted,
        duplicate_rows: importRows.filter((r) => r.status === 'duplicate').length,
        error_rows: importRows.filter((r) => r.status === 'error').length,
        status: 'completed',
      });

      setImportResult({
        imported: inserted,
        duplicates: importRows.filter((r) => r.status === 'duplicate').length,
        errors: importRows.filter((r) => r.status === 'error').length,
      });
      setStep('done');
      toast.success(`Successfully imported ${inserted} contacts!`);
    } catch (err: any) {
      toast.error('Import failed: ' + (err?.message ?? 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [importRows, fileName]);

  const handleReset = () => {
    setStep('upload');
    setFileName('');
    setCsvColumns([]);
    setRawRows([]);
    setMappings([]);
    setImportRows([]);
    setImportResult(null);
  };

  const currentStepIndex = STEPS.findIndex((s) => s.id === step);
  const validCount = importRows.filter((r) => r.status === 'valid').length;
  const dupCount = importRows.filter((r) => r.status === 'duplicate').length;
  const errCount = importRows.filter((r) => r.status === 'error').length;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-white shrink-0">
        <div>
          <h1 className="text-lg font-bold text-foreground">Import Contacts</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Upload a CSV or Excel file to bulk-import contacts into ShowCRM
          </p>
        </div>
        {step !== 'upload' && step !== 'done' && (
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-1.5 transition-colors"
          >
            <RotateCcw size={13} />
            Start Over
          </button>
        )}
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-0 px-6 py-3 border-b border-border bg-white shrink-0">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors
                ${i < currentStepIndex ? 'bg-primary text-white' : i === currentStepIndex ? 'bg-primary text-white ring-4 ring-primary/20' : 'bg-muted text-muted-foreground'}`}>
                {i < currentStepIndex ? <CheckCircle2 size={13} /> : i + 1}
              </div>
              <span className={`text-xs font-medium transition-colors ${i === currentStepIndex ? 'text-foreground' : 'text-muted-foreground'}`}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-px mx-3 transition-colors ${i < currentStepIndex ? 'bg-primary' : 'bg-border'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto">

          {/* Step 1: Upload */}
          {step === 'upload' && (
            <FileUploadZone onFileAccepted={handleFileAccepted} isLoading={isLoading} />
          )}

          {/* Step 2: Column Mapping */}
          {step === 'mapping' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-xl border border-border">
                <FileText size={16} className="text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-foreground truncate">{fileName}</p>
                  <p className="text-[11px] text-muted-foreground">{rawRows.length} rows · {csvColumns.length} columns</p>
                </div>
              </div>

              <ColumnMappingPanel
                csvColumns={csvColumns}
                mappings={mappings}
                onMappingChange={handleMappingChange}
                sampleRows={rawRows.slice(0, 3)}
              />

              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setStep('upload')}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-xl px-4 py-2 transition-colors"
                >
                  <ArrowLeft size={15} />
                  Back
                </button>
                <button
                  onClick={buildPreview}
                  disabled={isLoading}
                  className="flex items-center gap-2 text-sm font-semibold bg-primary text-white rounded-xl px-5 py-2 hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <ArrowRight size={15} />
                  )}
                  Preview Import
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && (
            <div className="space-y-6">
              {/* Summary bar */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Will Import', value: validCount, color: 'text-green-700 bg-green-50 border-green-200' },
                  { label: 'Duplicates (skip)', value: dupCount, color: 'text-amber-700 bg-amber-50 border-amber-200' },
                  { label: 'Errors (skip)', value: errCount, color: 'text-red-700 bg-red-50 border-red-200' },
                ].map(({ label, value, color }) => (
                  <div key={label} className={`flex flex-col items-center justify-center p-4 rounded-xl border ${color}`}>
                    <span className="text-2xl font-bold font-mono">{value}</span>
                    <span className="text-xs font-medium mt-1">{label}</span>
                  </div>
                ))}
              </div>

              <ImportPreviewTable rows={importRows} />

              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setStep('mapping')}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-xl px-4 py-2 transition-colors"
                >
                  <ArrowLeft size={15} />
                  Back
                </button>
                <button
                  onClick={handleConfirmImport}
                  disabled={isLoading || validCount === 0}
                  className="flex items-center gap-2 text-sm font-semibold bg-primary text-white rounded-xl px-5 py-2 hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Upload size={15} />
                  )}
                  Import {validCount} Contact{validCount !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Done */}
          {step === 'done' && importResult && (
            <div className="flex flex-col items-center justify-center py-16 gap-6">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 size={40} className="text-green-600" />
              </div>
              <div className="text-center">
                <h2 className="text-xl font-bold text-foreground">Import Complete!</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Your contacts have been added to ShowCRM.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4 w-full max-w-sm">
                {[
                  { label: 'Imported', value: importResult.imported, color: 'text-green-700 bg-green-50 border-green-200' },
                  { label: 'Skipped (dup)', value: importResult.duplicates, color: 'text-amber-700 bg-amber-50 border-amber-200' },
                  { label: 'Skipped (err)', value: importResult.errors, color: 'text-red-700 bg-red-50 border-red-200' },
                ].map(({ label, value, color }) => (
                  <div key={label} className={`flex flex-col items-center p-3 rounded-xl border ${color}`}>
                    <span className="text-xl font-bold font-mono">{value}</span>
                    <span className="text-[11px] font-medium mt-0.5">{label}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleReset}
                  className="flex items-center gap-1.5 text-sm border border-border rounded-xl px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <RotateCcw size={14} />
                  Import Another File
                </button>
                <a
                  href="/contacts-list"
                  className="flex items-center gap-1.5 text-sm font-semibold bg-primary text-white rounded-xl px-5 py-2 hover:bg-primary/90 transition-colors"
                >
                  View Contacts
                  <ArrowRight size={14} />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
